/**
 * Copyright IBM Corp. 2022, 2026
 */

import * as React from 'react'
import s from './style.module.css'

/**
 * Capitalize the first letter of a string
 * @param {string} string - The string to be capitalized
 */
function capitalize(string: string): string {
	return string.charAt(0).toUpperCase() + string.slice(1)
}

interface ProductMeta {
	name: string
	slug: string
	themeClass?: string
}

type Products =
	| 'hashicorp'
	| 'boundary'
	| 'consul'
	| 'nomad'
	| 'packer'
	| 'terraform'
	| 'vault'
	| 'vagrant'
	| 'waypoint'

const DEFAULT: ProductMeta = {
	name: 'Hashicorp',
	slug: 'hashicorp',
}

const ProductContext = React.createContext<ProductMeta>(DEFAULT)

/**
 * fn `useProductMeta` is designed to work both within and outside of a Product Context.
 * Components can pass a product name directly and get metadata outside of context,
 * or they can set the 'product' higher up in the tree via `ProductMetaProvider`, call
 * `useProductMeta` without passing a product argument, context will provide the correct metadata.
 *  */
function useProductMeta(_product?: Products): ProductMeta {
	const product = _product?.toLowerCase()
	const ctx = React.useContext(ProductContext) ?? DEFAULT
	const slug = product ?? ctx.slug
	const name = capitalize(product ?? ctx.name)
	const themeClass = product ? s[product] : ctx.themeClass // class resets base `brand` css variables per theme

	if (process.env.NODE_ENV !== 'production' && !ctx && !product) {
		console.warn(
			'@web/product-meta: No product context or product prop detected, HashiCorp theme & product defaults will apply.',
		)
	}

	return { name, slug, themeClass }
}

interface ProductProviderProps {
	product: Products
	children: React.ReactNode
}

const ProductMetaProvider = React.memo(function Provider({
	product,
	children,
}: ProductProviderProps) {
	const ctxValue = useProductMeta(product)
	return (
		<ProductContext.Provider value={ctxValue}>
			{children}
		</ProductContext.Provider>
	)
})

export default useProductMeta
export type { Products, ProductMeta }
export { ProductContext, ProductMetaProvider }
